import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Karigars from "@/pages/Karigars";
import KarigarProfile from "@/pages/KarigarProfile";
import Production from "@/pages/Production";
import Stock from "@/pages/Stock";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import BALayout from "@/pages/accounts/BALayout";
import BADashboard from "@/pages/accounts/BADashboard";
import BAEntries from "@/pages/accounts/BAEntries";
import BAReports from "@/pages/accounts/BAReports";
import BASettings from "@/pages/accounts/BASettings";

const AuthContext = createContext(null);
const ThemeContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export const useTheme = () => useContext(ThemeContext);

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem("an_theme") || "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("an_theme", theme);
  }, [theme]);

  useEffect(() => {
    const token = localStorage.getItem("an_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem("an_token"))
      .finally(() => setChecking(false));
  }, []);

  const login = (token, u) => {
    localStorage.setItem("an_token", token);
    setUser(u);
  };
  const logout = () => {
    localStorage.removeItem("an_token");
    setUser(null);
  };
  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <BrowserRouter>
          {user ? (
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customers/:id" element={<CustomerDetail />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/orders/:id" element={<OrderDetail />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:oid/:pid" element={<ProductDetail />} />
                <Route path="/karigars" element={<Karigars />} />
                <Route path="/karigars/:id" element={<KarigarProfile />} />
                <Route path="/production" element={<Production />} />
                <Route path="/stock" element={<Stock />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/accounts" element={<BALayout />}>
                  <Route index element={<BADashboard />} />
                  <Route path="sales" element={<BAEntries type="sales" />} />
                  <Route path="expenses" element={<BAEntries type="expenses" />} />
                  <Route path="suppliers" element={<BAEntries type="supplier-payments" />} />
                  <Route path="salaries" element={<BAEntries type="salaries" />} />
                  <Route path="income" element={<BAEntries type="other-income" />} />
                  <Route path="reports" element={<BAReports />} />
                  <Route path="settings" element={<BASettings />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          ) : (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          )}
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </ThemeContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;

